import React, { useState, useEffect } from 'react';
import { 
  FaUsers, FaSearch, FaSync, FaClock, 
  FaCheckCircle, FaTimesCircle, FaEnvelope, FaPhone 
} from 'react-icons/fa';

// --- Types ---
interface Seller {
  id: number;
  companyName: string;
  username: string;
  email: string;
  mobile: string;
  isVerified: boolean;
  lastActiveTime?: string; // ISO String
  totalDurationMinutes?: number; // Total activity
}

const SellerListPage: React.FC = () => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // --- 1. Fetch Sellers ---
  const fetchSellers = async () => {
    try {
      const response = await fetch('/springboot/api/admin/sellers-activity');
      if (response.ok) {
        const data = await response.json();
        setSellers(data);
      } else {
        console.error("Failed to fetch sellers");
      }
    } catch (error) {
      console.error("Network Error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSellers();
    const interval = setInterval(fetchSellers, 30000); 
    return () => clearInterval(interval);
  }, []);

  // --- Helpers ---
  const isUserOnline = (lastActive?: string) => {
    if (!lastActive) return false;
    const diffMinutes = (new Date().getTime() - new Date(lastActive).getTime()) / (1000 * 60);
    return diffMinutes <= 2; 
  };

  const formatLastSeen = (dateString?: string) => {
    if (!dateString) return "Never";
    const diffSeconds = Math.floor((new Date().getTime() - new Date(dateString).getTime()) / 1000);
    if (diffSeconds < 60) return "Just now";
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} mins ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)} hrs ago`;
    return new Date(dateString).toLocaleDateString(); 
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return "0m";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  // --- Filter ---
  const filteredSellers = sellers.filter(s => 
    s.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-20 font-sans animate-fade-in-up">
      <div className="max-w-[1600px] mx-auto space-y-6">
        
        {/* 1. HEADER */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
             <h1 className="text-2xl font-black text-gray-900 flex items-center gap-3">
               <span className="p-2 bg-[#006d5b]/10 text-[#006d5b] rounded-lg">
                 <FaUsers size={20} />
               </span>
               Seller Activity Log
             </h1>
             <p className="text-gray-500 text-xs font-bold mt-1 ml-1">
               Real-time monitoring of seller online status & engagement.
             </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
             <div className="relative group w-full sm:w-64">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#006d5b]" />
                <input 
                  type="text" 
                  placeholder="Search sellers..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl py-2.5 pl-10 pr-4 text-xs font-bold focus:ring-2 focus:ring-[#006d5b]/20 focus:border-[#006d5b] outline-none transition-all"
                />
             </div>
             <button 
               onClick={() => { setLoading(true); fetchSellers(); }} 
               className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-600 px-4 py-2.5 rounded-xl hover:bg-[#006d5b] hover:text-white hover:border-[#006d5b] transition-all shadow-sm font-bold text-xs"
             >
                <FaSync className={loading ? "animate-spin" : ""} /> Refresh
             </button>
          </div>
        </div>

        {/* 2. TABLE (Desktop View) */}
        <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50/50 border-b border-gray-100 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <tr>
                <th className="p-5">Status</th>
                <th className="p-5">Seller Profile</th>
                <th className="p-5">Contact Details</th>
                <th className="p-5">Last Seen</th>
                <th className="p-5">Total Active Time</th>
                <th className="p-5 text-center">Verified</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 text-sm">
              {filteredSellers.length === 0 ? (
                 <tr><td colSpan={6} className="p-8 text-center text-gray-400 font-bold text-xs">No active sellers found.</td></tr>
              ) : (
                filteredSellers.map((seller) => {
                  const online = isUserOnline(seller.lastActiveTime);
                  return (
                    <tr key={seller.id} className="hover:bg-gray-50/50 transition-colors group">
                      
                      {/* Status - REVERSED LOGIC */}
                      <td className="p-5">
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide border ${
                          online 
                            ? 'bg-red-50 text-red-700 border-red-100 shadow-sm'    // Online = RED + Blink
                            : 'bg-green-50 text-green-700 border-green-100'        // Normal = GREEN
                        }`}>
                          <div className={`relative flex h-2 w-2`}>
                            {online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>}
                            <span className={`relative inline-flex rounded-full h-2 w-2 ${online ? 'bg-red-500' : 'bg-green-500'}`}></span>
                          </div>
                          {online ? 'ONLINE' : 'OFFLINE'}
                        </div>
                      </td>

                      {/* Profile */}
                      <td className="p-5">
                         <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center font-black text-gray-500 border border-gray-200 uppercase">
                               {seller.companyName?.charAt(0)}
                            </div>
                            <div>
                               <p className="font-bold text-gray-900 text-sm">{seller.companyName || "N/A"}</p>
                               <p className="text-[10px] text-gray-400 font-bold uppercase">{seller.username}</p>
                            </div>
                         </div>
                      </td>

                      {/* Contact */}
                      <td className="p-5">
                         <div className="space-y-1">
                            <div className="flex items-center gap-2 text-xs font-medium text-gray-600">
                               <FaEnvelope className="text-gray-300" /> {seller.email}
                            </div>
                            <div className="flex items-center gap-2 text-xs font-medium text-gray-600">
                               <FaPhone className="text-gray-300" /> {seller.mobile || "N/A"}
                            </div>
                         </div>
                      </td>

                      {/* Last Seen */}
                      <td className="p-5">
                         <div className="flex items-center gap-2 text-xs font-bold text-gray-500">
                            <FaClock className="text-[#006d5b]/40" /> {formatLastSeen(seller.lastActiveTime)}
                         </div>
                      </td>

                      {/* Active Time */}
                      <td className="p-5">
                         <span className="px-3 py-1 bg-[#006d5b]/5 text-[#006d5b] rounded-lg text-xs font-black border border-[#006d5b]/10">
                            {formatDuration(seller.totalDurationMinutes)}
                         </span>
                      </td>

                      {/* Verified */}
                      <td className="p-5 text-center">
                         {seller.isVerified ? (
                            <FaCheckCircle className="text-[#006d5b] mx-auto text-lg" title="Verified" />
                         ) : (
                            <FaTimesCircle className="text-gray-300 mx-auto text-lg" title="Not Verified" />
                         )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 3. CARDS (Mobile View) */}
        <div className="md:hidden grid grid-cols-1 gap-4">
           {filteredSellers.map((seller) => {
              const online = isUserOnline(seller.lastActiveTime);
              return (
                 <div key={seller.id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                    {/* Status Badge Top Right - REVERSED LOGIC */}
                    <div className="absolute top-4 right-4">
                       {online ? (
                          <span className="flex items-center gap-1.5 px-2 py-1 bg-red-50 text-red-700 rounded-md text-[10px] font-black border border-red-100 shadow-sm">
                             <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span> ONLINE
                          </span>
                       ) : (
                          <span className="px-2 py-1 bg-green-50 text-green-700 rounded-md text-[10px] font-black border border-green-100">
                             OFFLINE
                          </span>
                       )}
                    </div>

                    <div className="flex items-center gap-4 mb-4">
                       <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center font-black text-gray-500 border border-gray-200 text-lg uppercase">
                          {seller.companyName?.charAt(0)}
                       </div>
                       <div>
                          <h3 className="font-bold text-gray-900 text-sm">{seller.companyName}</h3>
                          <p className="text-xs text-gray-400 font-bold uppercase">{seller.username}</p>
                       </div>
                    </div>

                    <div className="space-y-3 pt-3 border-t border-gray-50">
                       <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-gray-400">Total Active Time</span>
                          <span className="font-black text-[#006d5b] bg-[#006d5b]/5 px-2 py-0.5 rounded">
                             {formatDuration(seller.totalDurationMinutes)}
                          </span>
                       </div>
                       <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-gray-400">Last Seen</span>
                          <span className="font-bold text-gray-700">{formatLastSeen(seller.lastActiveTime)}</span>
                       </div>
                       <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-gray-400">Verification</span>
                          {seller.isVerified ? (
                             <span className="flex items-center gap-1 text-[#006d5b] font-black"><FaCheckCircle/> Verified</span>
                          ) : (
                             <span className="flex items-center gap-1 text-gray-400 font-black"><FaTimesCircle/> Pending</span>
                          )}
                       </div>
                    </div>
                 </div>
              );
           })}
        </div>

      </div>
    </div>
  );
};

export default SellerListPage;